/**
 * License-rank allocation budget + gate — the single source the reducer wall
 * (`set_license_rank`, and later the equip gate) and the editor kit affordance
 * both import, so the cap can never drift between the gate and the control. The
 * same discipline as `budgets.ts` (HASE/skill/talent caps) and `sp.ts`.
 *
 * **The pacing model is a snapshot invariant, NOT a per-level ledger**: a pilot
 * accrues 1 license rank per level (total ranks
 * = LL, Core p. 18), and RAW lets a pilot freely *reallocate all ranks* on any
 * level-up — which collapses the reachable set to exactly two constraints:
 *
 *   - Σ(license ranks) ≤ LL          (the per-LL budget)
 *   - each license rank ≤ 3          (a frame license is a 3-rank ladder)
 *
 * So ranks are freely re-editable anytime; there is no per-level history to track
 * (matches COMP/CON's `MaxPoints = Level` cap). There is **no "items per increase"
 * cap** — RAW grants unlimited access to all gear at unlocked ranks; quantity is
 * governed by mounts/SP, and the license gate is purely rank ≥ the item's
 * `license_level`.
 *
 * GMS is **seeded** as an explicit max-rank license on every pilot (migration +
 * `create_pilot`), so the equip gate needs no GMS special-case — and that seeded
 * rank is **free** (it does NOT count against the per-LL budget; it sits at LL0).
 * Only non-GMS ranks are budgeted.
 */
import type { LicenseRank, PilotData } from "./types";

/** Max rank in a single frame license (the I/II/III ladder). */
export const MAX_LICENSE_RANK = 3;

/**
 * The seeded, free GMS license id. Every pilot carries a max-rank GMS entry so GMS
 * gear (Everest, the GMS weapons/systems) passes the equip gate with no special
 * case. The id is the slug of the license name "GMS" (the same derivation the
 * compendium's synthesized license catalog uses — `slug("GMS") === "gms"`).
 */
export const GMS_LICENSE_ID = "gms";

/** A max-rank GMS license entry — what migration and `create_pilot` seed. */
export function seededGmsLicense(): LicenseRank {
  return { id: GMS_LICENSE_ID, rank: MAX_LICENSE_RANK };
}

/**
 * The license-rank budget at a pilot's level: total spendable ranks = LL (1 rank
 * per level, Core p. 18). GMS is free and excluded from the spend (see {@link
 * spentLicenseRanks}), so this is the cap on *non-GMS* ranks.
 */
export function licenseBudget(pilot: PilotData): number {
  return pilot.level;
}

/** The rank a pilot currently holds in a license (0 if unowned). */
export function licenseRankOf(pilot: PilotData, licenseId: string): number {
  return pilot.licenses.find((l) => l.id === licenseId)?.rank ?? 0;
}

/**
 * Σ of the budgeted license ranks — every license except the free, seeded GMS one.
 * This is what the per-LL budget gates: GMS sits at LL0 and never costs a rank.
 */
export function spentLicenseRanks(pilot: PilotData): number {
  let total = 0;
  for (const l of pilot.licenses) {
    if (l.id === GMS_LICENSE_ID) continue;
    total += l.rank;
  }
  return total;
}

/**
 * Set one license's rank to an absolute value in a `LicenseRank[]`, or remove it
 * when `rank === 0`. Returns a new array (immutable) — the swap that backs both the
 * forward `set_license_rank` command and its symmetric inverse. Keeps the array
 * ordering stable (in-place replace for an existing id, append for a new one) so
 * the inverse round-trip is a deep-equal.
 */
export function withLicenseRank(
  licenses: LicenseRank[],
  id: string,
  rank: number,
): LicenseRank[] {
  if (rank === 0) return licenses.filter((l) => l.id !== id);
  if (licenses.some((l) => l.id === id)) {
    return licenses.map((l) => (l.id === id ? { id, rank } : l));
  }
  return [...licenses, { id, rank }];
}

// ── The equip gate: rank ≥ the item's license_level ───────────────────────────────
//
// The single source the reducer wall (`select_frame`/`equip_weapon`/`equip_system`/
// `attach_mod`) imports, and the kit affordance mirrors. The rule is the pure RAW
// shape: a pilot may equip an item only
// when its rank in the item's license ≥ the item's `license_level`. There is NO
// per-increase item cap and NO unlocked-item inventory — quantity is governed by
// mounts/SP, the license gate is purely rank ≥ level.
//
// The required `(license, license_level)` is read off the **compendium item at equip
// time** (the reducer is reference-dependent here, like `select_frame`), never a wire
// field. License id derivation mirrors the synthesized license catalog exactly:
//   - a FRAME carries no `license` field — its license is named "GMS" when its
//     `source` is GMS, else the frame's own `name`; its level is `license_level`;
//   - a weapon/system/mod declares its `license` name directly, level `license_level`.
// Items with no license / level 0 are always allowed. GMS needs no special-case: every
// pilot carries a seeded max-rank GMS entry, so GMS gear's required rank is covered.

/** The minimal shape of a compendium item the gate reads (all fields optional/passthrough). */
export type LicensedItem = {
  name?: string;
  source?: string;
  license?: string;
  license_level?: number;
};

/**
 * The `(license id, required rank)` an item demands, or `null` when the item is
 * unlicensed / unlocked at rank 0 (always equippable). `type` distinguishes a frame
 * (license = its own name, or "GMS" by source) from gear (license = its `license`
 * field). The id is the same `slug(name)` the pilot's `licenses` are keyed by.
 */
export function licenseRequirement(
  item: LicensedItem,
  type: "frame" | "weapon" | "system" | "mod",
): { id: string; level: number } | null {
  const level = item.license_level ?? 0;
  const name = type === "frame" ? (item.source === "GMS" ? "GMS" : item.name) : item.license;
  if (!name || level <= 0) return null;
  return { id: slug(name), level };
}

/**
 * Does the pilot meet the license requirement to equip this compendium item? `true`
 * when the item is unlicensed/rank-0, or when the pilot's earned rank in the item's
 * license ≥ the required `license_level`. GMS gear passes via the seeded GMS rank — no
 * special case. The single predicate the equip-gate reducer rules and the kit
 * affordance both consult, so the wall and the affordance can't drift.
 */
export function meetsLicenseRequirement(
  pilot: PilotData,
  item: LicensedItem,
  type: "frame" | "weapon" | "system" | "mod",
): boolean {
  const req = licenseRequirement(item, type);
  if (!req) return true;
  return licenseRankOf(pilot, req.id) >= req.level;
}

// ── The per-manufacturer rank-count seam ──────────────────────────────────────────
//
// Core bonuses unlock per manufacturer once a pilot has accrued enough ranks across
// that manufacturer's licenses. License id → manufacturer is NOT carried on
// `PilotData` (raw state stays minimal); it's resolved against the read-only
// compendium, exactly like the equip gate resolves an item's required rank.

/**
 * The minimal compendium shape this helper needs: list frames/weapons/systems/mods
 * so it can map a license id back to its manufacturer (`source`). Mirrors `sp.ts`'s
 * `SpCompendium` — a structural slice, so the domain package depends on the
 * compendium's *types* only and never the frontend's synthesized catalog.
 */
export type LicenseCompendium = {
  list<T extends "frame" | "weapon" | "system" | "mod">(
    type: T,
  ): { source?: string; license?: string; name?: string }[];
};

/** lower-case + collapse non-alphanumerics to hyphens (the license-id slug). */
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Build a `license id → manufacturer id (source)` map from the compendium, mirroring
 * the synthesized license catalog's derivation:
 *   - a frame's license is named "GMS" when its `source` is GMS, else its frame name;
 *   - a weapon/system/mod's license is its `license` field (skipped when absent).
 * The license id is `slug(name)`; the manufacturer is the item's `source`.
 */
export function licenseManufacturers(
  compendium: LicenseCompendium,
): Record<string, string> {
  const out: Record<string, string> = {};
  const add = (name: string | undefined, source: string | undefined): void => {
    if (!name || !source) return;
    out[slug(name)] = source;
  };
  for (const f of compendium.list("frame")) {
    add(f.source === "GMS" ? "GMS" : f.name, f.source);
  }
  for (const type of ["weapon", "system", "mod"] as const) {
    for (const it of compendium.list(type)) add(it.license, it.source);
  }
  return out;
}

/**
 * The **per-manufacturer earned rank count**: total license ranks a pilot holds in
 * each manufacturer's licenses, grouping license id → manufacturer via the
 * compendium. This is the value the core-bonus prereq reads to decide which
 * manufacturer bonuses a pilot has unlocked.
 * GMS is included (a pilot's seeded GMS rank counts toward GMS); callers that want
 * "earned, spendable" ranks can subtract the GMS contribution.
 */
export function ranksByManufacturer(
  pilot: PilotData,
  compendium: LicenseCompendium,
): Record<string, number> {
  const mfrOf = licenseManufacturers(compendium);
  const out: Record<string, number> = {};
  for (const l of pilot.licenses) {
    const mfr = mfrOf[l.id];
    if (!mfr) continue;
    out[mfr] = (out[mfr] ?? 0) + l.rank;
  }
  return out;
}

// ── Core-bonus prerequisite gate ──────────────────────────────────────────────────
//
// A pilot may TAKE a core bonus only when it meets the bonus's manufacturer-rank
// prerequisite (and, separately, the per-LL budget in `budgets.ts`). The single
// predicate the reducer wall (`set_core_bonus`) and the kit affordance both
// import, so the gate and the control can't drift. Manufacturer is the bonus's
// `source` — derived from the compendium entry, never stored on the pilot.

/** The GMS manufacturer source string. GMS core bonuses are always available (no rank gate). */
export const GMS_SOURCE = "GMS";

/** The minimal core-bonus shape the prereq reads: its manufacturer is its `source`. */
export type CoreBonusRef = { source?: string };

/**
 * Does the pilot meet the **manufacturer-rank prerequisite** to take `bonus`, given
 * how many bonuses from the same manufacturer it already holds (`alreadyHeld`)?
 *
 * RAW (Core p. 34/116): a Big-Four bonus needs 3 license ranks with that
 * manufacturer; a 2nd from the same manufacturer needs 6, etc. — so a pilot may hold
 * `floor(ranksInMfr / 3)` bonuses from it, and taking the `alreadyHeld+1`-th requires
 * `floor(ranks/3) ≥ alreadyHeld + 1`. **GMS is exempt** (and any unsourced bonus):
 * always available, never routed through the rank rule (don't seed-and-floor it like
 * the equip gate — GMS core bonuses have no rank requirement at all). Reads
 * {@link ranksByManufacturer} (which keys on the same `source` strings the bonus does).
 *
 * Args are bundled (the reducer wall + the kit affordance both call this; an
 * options object keeps it under the 3-param lint cap and reads at the call site).
 */
export function meetsCoreBonusPrereq(args: {
  pilot: PilotData;
  bonus: CoreBonusRef;
  compendium: LicenseCompendium;
  /** How many bonuses from the SAME manufacturer the pilot already holds. */
  alreadyHeld: number;
}): boolean {
  const { pilot, bonus, compendium, alreadyHeld } = args;
  const mfr = bonus.source;
  if (!mfr || mfr === GMS_SOURCE) return true;
  const ranks = ranksByManufacturer(pilot, compendium)[mfr] ?? 0;
  return Math.floor(ranks / 3) >= alreadyHeld + 1;
}

/**
 * Add or remove a core-bonus id in a flat id list (immutable). `taken: true` appends
 * the id (no-op if already present); `taken: false` removes it. Returns a new array —
 * the swap that backs both the forward `set_core_bonus` command and its symmetric
 * inverse. Mirrors `withLicenseRank`'s shape for the rankless id-list model.
 */
export function withCoreBonus(coreBonuses: string[], id: string, taken: boolean): string[] {
  if (!taken) return coreBonuses.filter((cb) => cb !== id);
  if (coreBonuses.includes(id)) return coreBonuses;
  return [...coreBonuses, id];
}
